/**
 * responsive-variant-picker
 *
 * The variant picker renders two views: `[data-variant-view="desktop"]` (the
 * pickers configured in the theme editor) and `[data-variant-view="mobile"]`
 * (boxed dropdowns). Only one of them may exist in the DOM at a time, because
 * <variant-selects> reads every checked input and select inside itself when
 * building the option_values request — two views would send duplicates.
 *
 * This element detaches the inactive view (leaving a comment placeholder so
 * source order is kept) and puts it back when the breakpoint flips. CSS in
 * component-product-variant-picker.css hides the wrong view before this runs,
 * so there is no flash on first paint and no broken layout without JS.
 */
if (!customElements.get('responsive-variant-picker')) {
  customElements.define(
    'responsive-variant-picker',
    class ResponsiveVariantPicker extends HTMLElement {
      views = [];
      mediaQuery = undefined;
      onChange = undefined;

      connectedCallback() {
        const breakpoint = parseInt(this.dataset.breakpoint, 10) || 768;

        this.views = Array.from(this.querySelectorAll('[data-variant-view]')).map((element) => ({
          element,
          view: element.dataset.variantView,
          placeholder: document.createComment(`variant-view:${element.dataset.variantView}`),
        }));

        if (!this.views.length) return;

        this.mediaQuery = window.matchMedia(`(min-width: ${breakpoint}px)`);
        this.onChange = () => this.sync();

        if (this.mediaQuery.addEventListener) {
          this.mediaQuery.addEventListener('change', this.onChange);
        } else {
          // Safari < 14
          this.mediaQuery.addListener(this.onChange);
        }

        this.sync();
      }

      disconnectedCallback() {
        if (!this.mediaQuery || !this.onChange) return;

        if (this.mediaQuery.removeEventListener) {
          this.mediaQuery.removeEventListener('change', this.onChange);
        } else {
          this.mediaQuery.removeListener(this.onChange);
        }
      }

      sync() {
        const activeView = this.mediaQuery.matches ? 'desktop' : 'mobile';

        this.views.forEach(({ element, view, placeholder }) => {
          const shouldBeActive = view === activeView;

          if (shouldBeActive && !element.isConnected && placeholder.isConnected) {
            placeholder.replaceWith(element);
          } else if (!shouldBeActive && element.isConnected) {
            element.replaceWith(placeholder);
          }
        });
      }
    }
  );
}
